import Image from "next/image";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import classes from "./EMIOptions.module.css";
import { numberWithCommas, toTwoDigit } from "@/utils";
import { RightIcon } from "@/components/Icons";
import Loader from "@/components/Loader";
import { getActiveEmiBanks, EmiBank } from "@/requests/emiPlan";
import { getS3ImageUrl } from "@/utils/routes";

interface Props {
  className?: string;
  style?: CSSProperties;
  price: number;
  slug?: string;
}

interface EmiPlan {
  id: number;
  emi_bank_id?: number;
  month: number;
  charged: number;
}

const EMIOptions = ({ className, style, price, slug }: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [banks, setBanks] = useState<EmiBank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");

    getActiveEmiBanks(slug || "")
      .then((response) => {
        if (!mounted) return;

        if (!response.Success) {
          setError(response.Message || "Unable to load EMI banks.");
          return;
        }

        setBanks(response.Data || []);
      })
      .catch(() => {
        if (mounted) setError("Unable to load EMI banks.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (activeIndex >= banks.length) {
      setActiveIndex(0);
    }
  }, [banks.length, activeIndex]);

  const plans = useMemo(() => {
    const rawPlans = banks[activeIndex]?.plans;

    if (!rawPlans) return [];

    let parsedPlans: EmiPlan[] = [];

    if (Array.isArray(rawPlans)) {
      parsedPlans = rawPlans as EmiPlan[];
    } else if (typeof rawPlans === "string") {
      try {
        parsedPlans = JSON.parse(rawPlans) as EmiPlan[];
      } catch {
        parsedPlans = [];
      }
    }

    return parsedPlans
      .map((plan) => {
        const months = Number(plan.month);
        const charged = Number(plan.charged || 0);

        if (
          !Number.isFinite(months) ||
          months <= 0 ||
          !Number.isFinite(charged)
        ) {
          return null;
        }

        const charge = Math.ceil((price * charged) / 100);
        const effectiveCost = price + charge;

        return {
          id: plan.id,
          months,
          charged,
          monthly: Math.ceil(effectiveCost / months),
          charge,
          effectiveCost,
        };
      })
      .filter((plan) => plan !== null);
  }, [activeIndex, banks, price]);

  const classNames = [classes.EMIOptions, className];

  return (
    <div className={classNames.join(" ")} style={style}>
      {/* <div className={classes.head}>
        <p className={classes.title}>EMI Options</p>
      </div> */}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : error ? (
        <p className="py-8 text-center text-sm text-red-600">{error}</p>
      ) : banks.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">
          No active EMI banks found.
        </p>
      ) : (
        <div className={classes.contents}>
          <div className={classes.left}>
            <p className={classes.title}>EMI Options</p>
            <div className={classes.bankNames}>
              {banks.map((bank, index) => (
                <button
                  key={bank.id}
                  className={`${classes.bankName} ${
                    index === activeIndex ? classes.active : ""
                  }`}
                  onClick={() => setActiveIndex(index)}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded bg-white">
                    {bank.logo ? (
                      <Image
                        src={
                          bank.logo.startsWith("http")
                            ? bank.logo
                            : getS3ImageUrl(bank.logo, "MEDIA")
                        }
                        alt={bank.bank_name}
                        width={32}
                        height={32}
                        className="h-full w-full object-contain"
                        unoptimized
                      />
                    ) : (
                      <span className="text-xs font-bold text-gray-500">
                        {bank.bank_name.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </span>
                  <span className="truncate text-xs lg:text-[15px]">{bank.bank_name}</span>
                  {index === activeIndex && (
                    <RightIcon className={classes.icon} />
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className={classes.right}>
            <p className={classes.title}>
              {banks[activeIndex]?.bank_name} EMI Plans
            </p>
            <p className="mb-3 text-xs lg:text-sm text-gray-500">
              Product price: BDT {numberWithCommas(price)}
            </p>

            <table className={classes.table}>
              <thead>
                <tr>
                  <th className="text-left text-xs lg:text-sm">Months</th>
                  <th className="text-left text-xs lg:text-sm">Monthly EMI</th>
                  <th className="text-left text-xs lg:text-sm">Charge</th>
                  <th className="text-left text-xs lg:text-sm">Effective Cost</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id}>
                    <td className="text-left text-[10px] lg:text-sm">{toTwoDigit(plan.months)}</td>
                    <td className="text-left text-[10px] lg:text-sm">BDT {numberWithCommas(plan.monthly)}</td>
                    <td className="text-left text-[10px] lg:text-sm">
                      BDT {numberWithCommas(plan.charge)} ({plan.charged}%)
                    </td>
                    <td className="text-left text-[10px] lg:text-sm">
                      BDT {numberWithCommas(plan.effectiveCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EMIOptions;
